/**
 * `buildFlowGraph(workflow, steps, agents)` — pure builder that converts the
 * server-side workflow + steps + agents arrays into the React Flow `nodes`
 * and `edges` arrays consumed by the canvas.
 *
 * Implements the AC-1401 / AC-1377 P2.4 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md` (section 2.8).
 *
 * ## Why this exists
 *
 * The body of `useFlowStore.loadWorkflow` (in `src/store/index.ts`) used to
 * own three concerns at once:
 *
 *   1. The HTTP fetches (`getWorkflow`, `getWorkflowSteps`).
 *   2. The transformation from `WorkflowStep[]` → `Node[]` + `Edge[]`.
 *   3. The store mutation that wrote those arrays into `useFlowStore` state.
 *
 * After the AC-1377 React Query migration, concern (1) moves to the
 * `useWorkflow` / `useWorkflowSteps` hooks and concern (3) becomes a local
 * `useEffect` that calls `setNodes` / `setEdges` from the editor. Concern (2)
 * is genuine pure logic — extracting it here lets the editor consume RQ
 * cache data via `useMemo(() => buildFlowGraph(...), [...])` without having
 * to round-trip through the legacy store action.
 *
 * Keeping this function PURE is load-bearing:
 *
 *   - It is referentially transparent given identical `workflow`/`steps`/
 *     `agents` inputs, which is what `useMemo` requires for stable identity.
 *   - It does not read from any store (`useProjectStore.getState()` is gone —
 *     the agents lookup is now a parameter), which makes it trivial to unit
 *     test and reason about.
 *   - It does not write to any store. Callers handle the React Flow seed.
 *
 * ## Behavior
 *
 * The builder reproduces the EXACT graph topology the legacy `loadWorkflow`
 * action produced, so the canvas renders identically before and after the
 * migration. The legacy code path is documented in
 * `git log -p frontend/src/store/index.ts` against the AC-1401 commit. The
 * salient rules:
 *
 *   1. **Initiator detection.** If any step's `step_type` is one of
 *      `queue_read`, `user_prompt`, or `auto_prompt`, the canvas does NOT
 *      get a synthetic Start node — the initiator step IS the start. This
 *      matches the user-facing rule that initiator steps are workflow
 *      entry points and a separate Start node would be redundant.
 *
 *   2. **Synthetic Start node.** When no initiator step is present, a
 *      `start-node` is prepended at the workflow's stored
 *      `start_node_position_x` / `start_node_position_y` (defaulting to
 *      `(100, 100)` when the workflow has never had its position saved).
 *      An edge from the Start node to the first step is added.
 *
 *   3. **Step → Node mapping.** Each `WorkflowStep` becomes a React Flow
 *      `Node`:
 *
 *        | step_type     | nodeType     |
 *        |---------------|--------------|
 *        | user_prompt   | userPrompt   |
 *        | run_agent     | agent        |
 *        | conditional   | conditional  |
 *        | auto_prompt   | autoPrompt   |
 *        | queue_write   | queueWrite   |
 *        | queue_read    | queueRead    |
 *        | filter        | filter       |
 *        | (anything else) | default    |
 *
 *      Position falls back to `(100 + step_order * 200, 200)` when the step
 *      has no stored `position_x` / `position_y` — the legacy
 *      "auto-arrange new step" path.
 *
 *   4. **Agent enrichment.** For `run_agent` steps with an `agent_id`, the
 *      builder looks up the matching agent in the supplied `agents` array
 *      and attaches it as `node.data.agent`. If the agent is not found
 *      (e.g. it was deleted, or the agents list hasn't loaded yet), `agent`
 *      is `undefined` — the AgentNode handles that gracefully. Critically,
 *      we DO NOT throw or skip the step when the agent is missing — the
 *      legacy code path also tolerated this.
 *
 *   5. **Edges from `previous_step_id`.** For each step with a non-null
 *      `previous_step_id`, an edge is created from that previous step to
 *      the current step. If either endpoint cannot be located among the
 *      currently-built nodes (defensive — should never happen with a
 *      consistent server response), the edge is silently dropped.
 *
 *   6. **Add nodes for terminal endpoints.** Every non-`add` node that has
 *      no outgoing edge (i.e. it is a terminal node in the current graph)
 *      gets a synthetic `add-node-{id}` placed below it, connected via a
 *      vertical edge. The `add` node is what shows the "+" button users
 *      click to append a new step. EXCEPTION: nodes whose type is in
 *      `TERMINAL_NODE_TYPES` (currently just `queueWrite`) end the branch
 *      and do NOT get an Add Node — their branch is complete by definition.
 *
 *   7. **Empty workflow.** When `steps` is empty AND a Start node was
 *      created (i.e. there is no initiator), an `add-node-start` is
 *      appended below the Start node so the user can create their first
 *      step. When `steps` is empty AND there is no Start node (this should
 *      never happen — empty workflows always lack an initiator — but the
 *      legacy code path handled it), no add node is created.
 *
 * ## Constants
 *
 * The numeric constants (`288` parent width, `48` add-node width, `200`
 * vertical offset, `MarkerType.ArrowClosed` arrow head, `#3b82f6` accent
 * blue) are duplicated verbatim from the legacy code path. Changing them
 * here changes them on the canvas; if you need different defaults consult
 * the layout engine instead — that is the canonical place for rendering
 * geometry.
 *
 * ## Why a class isn't used
 *
 * The builder is stateless. A class would add the wrong cognitive overhead
 * (instances, methods, `this`) for something that is fundamentally a pure
 * data transformation. A single exported function with named locals is
 * clearer and matches the rest of the `src/utils` style.
 */

import { Edge, MarkerType, Node } from 'reactflow';

import { Agent, Workflow, WorkflowStep } from '../types';
import {
  determineEdgeType,
  selectEdgeHandles,
} from './layoutEngine';

/**
 * Step types whose nodes CANNOT have an Add Node appended — they are
 * terminal endpoints by design (a `queue_write` writes to a queue and ends
 * the current branch; chaining further steps off it is not the supported
 * flow). Kept as a `Set` for O(1) lookup; the literal values mirror the
 * `nodeType` strings produced by the step→node mapping above.
 */
const TERMINAL_NODE_TYPES = new Set<string>(['queueWrite']);

/**
 * Step types that act as their own workflow entry point. When ANY step in
 * the workflow has one of these types, the synthetic Start node is omitted
 * because the initiator step IS the start.
 */
const INITIATOR_STEP_TYPES = new Set<string>([
  'queue_read',
  'user_prompt',
  'auto_prompt',
]);

/**
 * Geometry constants duplicated from the legacy `loadWorkflow` body.
 *
 * - `PARENT_NODE_WIDTH`  matches the Tailwind `w-72` class on AgentNode and
 *   friends (72 * 4 = 288 px). Used to center add-nodes below their parent.
 * - `ADD_NODE_WIDTH`     matches the Tailwind `w-12` class on the AddNode
 *   component (12 * 4 = 48 px). Used to center the add-node horizontally.
 * - `ADD_NODE_VERTICAL_OFFSET` is the legacy 200 px gap below a parent.
 * - `ARROW_HEAD_COLOR`   is the React Flow accent blue used everywhere.
 */
const PARENT_NODE_WIDTH = 288;
const ADD_NODE_WIDTH = 48;
const ADD_NODE_VERTICAL_OFFSET = 200;
const ARROW_HEAD_COLOR = '#3b82f6';

/** Default Start node position when the workflow has no stored position. */
const DEFAULT_START_X = 100;
const DEFAULT_START_Y = 100;
const DEFAULT_STEP_X_BASE = 100;
const DEFAULT_STEP_X_STEP = 200;
const DEFAULT_STEP_Y = 200;

/**
 * Map a `WorkflowStep.step_type` to the React Flow `node.type` string.
 * Unknown step types fall back to `'default'` — the legacy code path did
 * the same. Centralized here so the mapping is in ONE place.
 */
function stepTypeToNodeType(stepType: string): string {
  switch (stepType) {
    case 'user_prompt':
      return 'userPrompt';
    case 'run_agent':
      return 'agent';
    case 'conditional':
      return 'conditional';
    case 'auto_prompt':
      return 'autoPrompt';
    case 'queue_write':
      return 'queueWrite';
    case 'queue_read':
      return 'queueRead';
    case 'filter':
      return 'filter';
    case 'rdap_lookup':
      return 'rdapLookup';
    default:
      return 'default';
  }
}

/**
 * Default node label when the step has neither a `label` nor a `description`.
 * Matches the legacy fallback table in `loadWorkflow`.
 */
const STEP_TYPE_DEFAULT_LABEL: Record<string, string> = {
  user_prompt: 'User Prompt',
  run_agent: 'Agent',
  auto_prompt: 'Auto Prompt',
  conditional: 'Conditional',
  queue_read: 'Queue Read',
  queue_write: 'Queue Write',
  filter: 'Filter',
  rdap_lookup: 'Domain Availability',
};

/**
 * Build the React Flow `nodes` and `edges` arrays for a workflow.
 *
 * @param workflow The workflow detail (used for the Start node position).
 *                 May be `null` / `undefined` while the detail is still
 *                 fetching — in that case the builder returns empty arrays.
 * @param steps    The list of `WorkflowStep` for this workflow. May be empty
 *                 (newly created workflow with no steps yet). May be
 *                 `null` / `undefined` while the steps query is fetching;
 *                 the builder returns empty arrays in that case.
 * @param agents   Project agents used to enrich `run_agent` step nodes with
 *                 `data.agent`. Pass an empty array when agents are still
 *                 fetching — the steps still render, just without agent
 *                 metadata. NEVER pass `useProjectStore.getState().agents`
 *                 from inside this function — the agents must be a parameter
 *                 so the function stays pure and testable.
 *
 * @returns `{ nodes, edges }` ready to feed into React Flow. Always defined,
 *          may be empty arrays when there is nothing to render.
 *
 * @example
 *   // Inside WorkflowEditor:
 *   const { data: workflow } = useWorkflow(workflowId);
 *   const { data: steps }    = useWorkflowSteps(workflowId);
 *   const { data: agents }   = useAgents(projectId); // future hook
 *   const { nodes: seedNodes, edges: seedEdges } = useMemo(
 *     () => buildFlowGraph(workflow, steps ?? [], agents ?? []),
 *     [workflow, steps, agents],
 *   );
 *   useEffect(() => {
 *     setNodes(seedNodes);
 *     setEdges(seedEdges);
 *   }, [seedNodes, seedEdges]);
 */
export function buildFlowGraph(
  workflow: Workflow | null | undefined,
  steps: WorkflowStep[] | null | undefined,
  agents: Agent[] | null | undefined,
): { nodes: Node[]; edges: Edge[] } {
  // Defensive empties: callers may invoke this while their queries are still
  // fetching, in which case there is nothing to render. Returning empty
  // arrays avoids a render-time crash AND keeps the canvas blank rather
  // than flashing a stale graph.
  if (!workflow || !steps) {
    return { nodes: [], edges: [] };
  }

  const safeAgents = agents ?? [];

  // Initiator detection — see rule 1 in the file header.
  const hasInitiatorStep = steps.some((step) =>
    INITIATOR_STEP_TYPES.has(step.step_type),
  );

  let nodes: Node[] = [];
  let edges: Edge[] = [];

  // Rule 2: synthetic Start node when there is no initiator.
  if (!hasInitiatorStep) {
    const startNode: Node = {
      id: 'start-node',
      type: 'start',
      position: {
        x: workflow.start_node_position_x ?? DEFAULT_START_X,
        y: workflow.start_node_position_y ?? DEFAULT_START_Y,
      },
      data: {
        label: 'Start',
      },
    };
    nodes = [startNode];
  }

  if (steps.length > 0) {
    // Rule 3: convert steps to nodes, with rule 4 agent enrichment.
    const stepNodes: Node[] = steps.map((step, index) => {
      const nodeType = stepTypeToNodeType(step.step_type);

      // Rule 4: agent lookup. Missing agent => undefined (NOT an error).
      const agent =
        step.agent_id !== undefined && step.agent_id !== null
          ? safeAgents.find((a) => a.agent_id === step.agent_id)
          : undefined;

      const fallbackLabel =
        STEP_TYPE_DEFAULT_LABEL[step.step_type] ?? step.step_type;

      // AC-1625: guard positions with Number.isFinite (NOT `typeof`).
      // `typeof NaN === 'number'` was the original bug: the assistant
      // persisted NULL positions and omitted step_order, so the fallback
      // `100 + undefined * 200` produced NaN — and the old typeof check let
      // it through. Number.isFinite rejects NaN/Infinity, and step_order
      // falls back to the node's array index so the fallback is always a
      // finite number (never multiplied by undefined).
      const order = Number.isFinite(step.step_order)
        ? (step.step_order as number)
        : index;

      return {
        id: String(step.orchestration_step_id),
        type: nodeType,
        position: {
          x: Number.isFinite(step.position_x)
            ? (step.position_x as number)
            : DEFAULT_STEP_X_BASE + order * DEFAULT_STEP_X_STEP,
          y: Number.isFinite(step.position_y)
            ? (step.position_y as number)
            : DEFAULT_STEP_Y,
        },
        data: {
          id: String(step.orchestration_step_id),
          label: step.label || step.description || fallbackLabel,
          type: step.step_type,
          step,
          agent,
        },
      };
    });
    nodes = [...nodes, ...stepNodes];

    // Rule 5: edges from `previous_step_id`. Filter out edges whose
    // endpoints are missing from the node list — defensive but matches the
    // legacy behavior.
    edges = steps
      .filter((step) => step.previous_step_id)
      .map<Edge | null>((step) => {
        const sourceNode = nodes.find(
          (n) => n.id === String(step.previous_step_id),
        );
        const targetNode = nodes.find(
          (n) => n.id === String(step.orchestration_step_id),
        );
        if (!sourceNode || !targetNode) return null;

        const { sourceHandle, targetHandle } = selectEdgeHandles(
          sourceNode,
          targetNode,
          nodes,
        );

        return {
          id: `e-${step.previous_step_id}-${step.orchestration_step_id}`,
          source: String(step.previous_step_id),
          target: String(step.orchestration_step_id),
          type: determineEdgeType(sourceNode, targetNode),
          data: {},
          sourceHandle,
          targetHandle,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 24,
            height: 24,
            color: ARROW_HEAD_COLOR,
          },
        };
      })
      .filter((e): e is Edge => e !== null);

    // Rule 2 continued: connect Start node to first step (only when both
    // exist — i.e. there is no initiator).
    const firstStep = steps[0];
    if (firstStep && !hasInitiatorStep) {
      const startNode = nodes.find((n) => n.id === 'start-node');
      const targetNode = nodes.find(
        (n) => n.id === String(firstStep.orchestration_step_id),
      );
      if (startNode && targetNode) {
        const { sourceHandle, targetHandle } = selectEdgeHandles(
          startNode,
          targetNode,
          nodes,
        );
        edges.push({
          id: `e-start-${firstStep.orchestration_step_id}`,
          source: 'start-node',
          target: String(firstStep.orchestration_step_id),
          type: determineEdgeType(startNode, targetNode),
          data: {},
          sourceHandle,
          targetHandle,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 24,
            height: 24,
            color: ARROW_HEAD_COLOR,
          },
        });
      }
    }

    // Rule 6: append Add Nodes to terminal endpoints (skipping
    // TERMINAL_NODE_TYPES).
    const nodeIdsWithOutgoing = new Set(edges.map((e) => e.source));
    const nonAddNodeIds = nodes
      .filter((n) => n.type !== 'add')
      .map((n) => n.id);

    for (const nodeId of nonAddNodeIds) {
      if (nodeIdsWithOutgoing.has(nodeId)) continue;

      const parentNode = nodes.find((n) => n.id === nodeId);
      if (!parentNode) continue;

      // queue_write etc. — do not propose a "+" because the branch is done.
      if (TERMINAL_NODE_TYPES.has(parentNode.type ?? '')) continue;

      const parentX = parentNode.position.x;
      const parentY = parentNode.position.y;
      const addNodeX =
        parentX + PARENT_NODE_WIDTH / 2 - ADD_NODE_WIDTH / 2;
      const addNodeY = parentY + ADD_NODE_VERTICAL_OFFSET;
      const addNodeId = `add-node-${nodeId}`;

      nodes.push({
        id: addNodeId,
        type: 'add',
        position: { x: addNodeX, y: addNodeY },
        data: { parentNodeId: nodeId },
      });

      // Add-node edges are always vertical bottom→top regardless of layout.
      edges.push({
        id: `e-${nodeId}-add`,
        source: nodeId,
        target: addNodeId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'straight',
        data: {},
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 24,
          height: 24,
          color: ARROW_HEAD_COLOR,
        },
      });
    }
  } else {
    // Rule 7: empty steps + Start node => single Add Node below Start.
    const startNode = nodes.find((n) => n.id === 'start-node');
    if (startNode) {
      const addNodeId = 'add-node-start';
      const addNodeX =
        startNode.position.x +
        PARENT_NODE_WIDTH / 2 -
        ADD_NODE_WIDTH / 2;
      const addNodeY = startNode.position.y + ADD_NODE_VERTICAL_OFFSET;
      nodes.push({
        id: addNodeId,
        type: 'add',
        position: { x: addNodeX, y: addNodeY },
        data: { parentNodeId: 'start-node' },
      });
      edges.push({
        id: 'e-start-add',
        source: 'start-node',
        target: addNodeId,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'straight',
        data: {},
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 24,
          height: 24,
          color: ARROW_HEAD_COLOR,
        },
      });
    }
  }

  return { nodes, edges };
}

export default buildFlowGraph;
